import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { getIdTokenResult } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../config/firebase";
import { getStaffProvisioningEnvironment } from "../config/staffProvisioningEnvironment";
import { canAccessAdminPortal } from "../utils/adminRoles";
import "./styles/RestaurantPrinterRoutingControls.css";

const MODELS = [{ value: "V-WRP2", label: "Volcora V-WRP2" }, { value: "TSP100IV", label: "Star TSP143IV-UE" }];
const hash = value => typeof value === "string" && /^[a-f0-9]{64}$/.test(value);
const validId = value => typeof value === "string" && /^[A-Za-z0-9_-]{1,128}$/.test(value);
const nonempty = value => typeof value === "string" && !!value.trim();
const cleanText = value => nonempty(value) && !/[\x00-\x1f\x7f]/.test(value); // eslint-disable-line no-control-regex
const unique = values => new Set(values).size === values.length;
const privateLan = value => {
	if (typeof value !== "string") return false;
	const parts = value.split(".");
	if (parts.length !== 4 || !parts.every(part => /^(0|[1-9][0-9]{0,2})$/.test(part) && Number(part) <= 255)) return false;
	const [a, b] = parts.map(Number);
	return a === 10 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168);
};
const printerFields = printer => ({ id: printer.id, label: printer.label, interfaceType: printer.interfaceType,
	identifier: printer.identifier, model: printer.model, enabled: printer.enabled });
const routingFields = value => ({ printers: value.printers.filter(printer => printer.id !== "legacy").map(printer => ({ ...printerFields(printer), label: printer.label.trim() })),
	defaultPrinterId: value.defaultPrinterId, backupPrinterIds: value.backupPrinterIds.filter(Boolean),
	deviceDefaults: value.deviceDefaults.map(({ deviceId, printerId }) => ({ deviceId, printerId })) });
const fingerprint = value => {
	const fields = routingFields(value);
	fields.printers.sort((a, b) => a.id.localeCompare(b.id));
	fields.deviceDefaults.sort((a, b) => a.deviceId.localeCompare(b.deviceId));
	return JSON.stringify(fields);
};
const validPrinter = printer => printer && validId(printer.id) && cleanText(printer.label) && printer.label.trim().length <= 60 &&
	printer.interfaceType === "Lan" && privateLan(printer.identifier) && MODELS.some(model => model.value === printer.model) &&
	typeof printer.enabled === "boolean";

function readRouting(value, restaurantId) {
	if (!value || value.restaurantId !== restaurantId || !hash(value.revision) || !Array.isArray(value.printers) ||
		!value.printers.every(printer => validPrinter(printer) && typeof printer.legacy === "boolean" && (printer.id === "legacy") === printer.legacy) ||
		value.printers.filter(printer => !printer.legacy).length > 20 || !unique(value.printers.map(printer => printer.id)) ||
		!Array.isArray(value.backupPrinterIds) || value.backupPrinterIds.length > 3 || !unique(value.backupPrinterIds) ||
		!Array.isArray(value.deviceDefaults) || value.deviceDefaults.length > 100 ||
		!value.deviceDefaults.every(item => item && hash(item.deviceId) && nonempty(item.printerId)) ||
		!unique(value.deviceDefaults.map(item => item.deviceId)) || !Array.isArray(value.devices) ||
		!value.devices.every(device => device && hash(device.deviceId) && typeof device.label === "string" && typeof device.eligible === "boolean") ||
		!unique(value.devices.map(device => device.deviceId))) throw new Error("Invalid routing response");
	// Independent legacy setup removal may leave references that this editor must let support repair.
	const known = id => value.printers.some(printer => printer.id === id && printer.enabled) ||
		(id === "legacy" && !value.printers.some(printer => printer.id === "legacy"));
	if (!(value.defaultPrinterId === null || known(value.defaultPrinterId)) || !value.backupPrinterIds.every(known) ||
		!value.deviceDefaults.every(item => known(item.printerId))) throw new Error("Invalid routing targets");
	return { restaurantId, revision: value.revision,
		printers: value.printers.map(printer => ({ ...printerFields(printer), legacy: printer.legacy })),
		defaultPrinterId: value.defaultPrinterId, backupPrinterIds: [...value.backupPrinterIds],
		deviceDefaults: value.deviceDefaults.map(({ deviceId, printerId }) => ({ deviceId, printerId })),
		devices: value.devices.map(({ deviceId, label, eligible }) => ({ deviceId, label, eligible })) };
}

function draftError(draft, snapshot) {
	if (draft.printers.length > 20) return "A maximum of 20 shared printers is allowed.";
	if (!draft.printers.every(validPrinter)) return "Each printer needs a name of 1 to 60 characters, supported model and canonical private LAN IPv4 address.";
	if (!unique(draft.printers.map(printer => printer.id)) || draft.printers.some(printer => printer.id === "legacy")) return "Printer identities require review.";
	const printers = [...draft.printers, ...snapshot.printers.filter(printer => printer.legacy)];
	if (!unique(printers.map(printer => printer.identifier))) return "Printer IP addresses must be unique, including disabled printers and the legacy printer.";
	const enabled = id => printers.some(printer => printer.id === id && printer.enabled);
	if (draft.defaultPrinterId === null ? printers.some(printer => printer.enabled) : !enabled(draft.defaultPrinterId)) return "Choose an enabled default printer whenever any printer is enabled.";
	const backups = draft.backupPrinterIds.filter(Boolean);
	if (backups.length > 3 || !unique(backups) || !backups.every(enabled) || backups.includes(draft.defaultPrinterId)) return "Backups must be up to three distinct enabled printers, different from the restaurant default.";
	if (draft.deviceDefaults.length > 100) return "A maximum of 100 device defaults is allowed.";
	if (!unique(draft.deviceDefaults.map(item => item.deviceId)) || !draft.deviceDefaults.every(item =>
		hash(item.deviceId) && enabled(item.printerId) && snapshot.devices.some(device => device.deviceId === item.deviceId && device.eligible))) {
		return "Device defaults require eligible enrolled devices and enabled printers. Clear unavailable assignments.";
	}
	return "";
}

const errorMessage = (error, saving) => {
	const code = String(error?.code || "").replace(/^functions\//, "");
	if (["unimplemented", "not-found"].includes(code)) return "Shared printer routing is unavailable in this environment. Existing printer and drawer setup is unchanged.";
	if (["permission-denied", "unauthenticated"].includes(code)) return "Scerv admin access is required. Sign in with an authorized support account.";
	if (code === "aborted") return "Routing changed on the server. Your draft is retained. Refresh before saving again.";
	if (["failed-precondition", "invalid-argument"].includes(code)) return "Routing was rejected. Review printers, assignments and reason, then refresh before saving again.";
	return saving ? "Save outcome unconfirmed. Your draft is retained. Refresh before another save." : "Could not load valid printer routing. Refresh to try again.";
};

function RoutingForm({ restaurantId, user, environment, projectId, client, authClient }) {
	const id = useId();
	const serviceApp = useRef(client.app).current, authApp = useRef(authClient.app).current;
	const [snapshot, setSnapshot] = useState(null), baseline = useRef(null);
	const [draft, setDraft] = useState(null), draftRef = useRef(null);
	const [reason, setReason] = useState(""), reasonRef = useRef("");
	const [busy, setBusy] = useState(false), [blocked, setBlocked] = useState(true), [scopeLost, setScopeLost] = useState(false);
	const [error, setError] = useState(""), [message, setMessage] = useState("");
	const alive = useRef(false), generation = useRef({ value: 0 }), pending = useRef(false);
	const sameScope = useCallback(() => functions === client && client.app === serviceApp && serviceApp.options.projectId === projectId &&
		auth === authClient && authClient.app === authApp && (!authApp || authApp.options.projectId === projectId) &&
		authClient.currentUser === user && getStaffProvisioningEnvironment() === environment,
	[client, serviceApp, projectId, authClient, authApp, user, environment]);
	const current = useCallback(ticket => alive.current && generation.current.value === ticket && sameScope(), [sameScope]);
	const authorize = useCallback(async ticket => {
		if (!current(ticket)) return false;
		const token = await getIdTokenResult(user);
		if (!current(ticket)) return false;
		const role = token?.claims?.role;
		if (typeof role !== "string" || role !== role.trim() || !canAccessAdminPortal(role)) throw Object.assign(new Error("Denied"), { code: "permission-denied" });
		return true;
	}, [current, user]);
	const apply = useCallback(saved => {
		baseline.current = saved; draftRef.current = routingFields(saved); reasonRef.current = "";
		setDraft(draftRef.current); setReason("");
	}, []);
	const finish = useCallback(ticket => {
		if (!alive.current || generation.current.value !== ticket) return;
		pending.current = false; setBusy(false);
		if (!sameScope()) { setScopeLost(true); setBlocked(true); setMessage(""); setError("Support account or environment changed. Reload this page before continuing."); }
	}, [sameScope]);
	const reload = useCallback(async () => {
		if (pending.current || !current(generation.current.value)) return;
		const ticket = ++generation.current.value;
		pending.current = true; setBusy(true); setBlocked(true); setError(""); setMessage("");
		try {
			if (!await authorize(ticket)) return;
			const response = await httpsCallable(client, "getScervPrinterRouting")({ restaurantId });
			if (!current(ticket)) return;
			const saved = readRouting(response.data, restaurantId);
			const dirty = baseline.current && (fingerprint(draftRef.current) !== fingerprint(baseline.current) || reasonRef.current !== "");
			// A dirty draft keeps its original revision; refresh never silently rebases a whole-library write.
			if (!dirty) apply(saved);
			setSnapshot(saved); setBlocked(false);
		} catch (failure) { if (current(ticket)) setError(errorMessage(failure, false)); }
		finally { finish(ticket); }
	}, [apply, authorize, client, current, finish, restaurantId]);
	useEffect(() => {
		const counter = generation.current;
		alive.current = true; reload();
		return () => { alive.current = false; counter.value++; pending.current = false; };
	}, [reload]);
	const edit = next => {
		if (pending.current || !current(generation.current.value)) return;
		draftRef.current = next; setDraft(next); setMessage("");
	};
	const stale = snapshot && baseline.current?.revision !== snapshot.revision;
	const dirty = draft && baseline.current && (fingerprint(draft) !== fingerprint(baseline.current) || reason !== "");
	const validation = draft && snapshot ? draftError(draft, snapshot) : "";
	const reasonValid = cleanText(reason) && reason.length <= 240;
	const save = async event => {
		event.preventDefault();
		if (pending.current || blocked || stale || !snapshot || !dirty || !current(generation.current.value)) return;
		if (validation || !reasonValid) { setError(validation || "Enter a support reason of 1 to 240 characters without control characters."); return; }
		const command = { restaurantId, expectedRevision: baseline.current.revision, ...routingFields(draft), reason: reason.trim() };
		const ticket = ++generation.current.value;
		pending.current = true; setBusy(true); setError(""); setMessage("");
		try {
			if (!await authorize(ticket)) return;
			const response = await httpsCallable(client, "saveScervPrinterRouting")(command);
			if (!current(ticket)) return;
			const saved = readRouting(response.data, restaurantId);
			if (response.data.success !== true || typeof response.data.changed !== "boolean" || fingerprint(saved) !== fingerprint(command)) throw new Error("Unconfirmed save");
			apply(saved); setSnapshot(saved); setBlocked(false);
			setMessage(response.data.changed ? "Shared printer routing saved." : "Routing already matches. No changes saved.");
		} catch (failure) { if (current(ticket)) { setBlocked(true); setError(errorMessage(failure, true)); } }
		finally { finish(ticket); }
	};
	const addPrinter = () => {
		if (!draft || draft.printers.length >= 20 || pending.current || !current(generation.current.value)) return;
		try {
			const bytes = window.crypto.getRandomValues(new Uint8Array(12));
			const printerId = `printer_${Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("")}`;
			edit({ ...draft, printers: [...draft.printers, { id: printerId, label: "", interfaceType: "Lan", identifier: "", model: "V-WRP2", enabled: true }] });
		} catch { setError("Could not create a printer identity. Reload before adding a printer."); }
	};
	const updatePrinter = (printerId, patch) => edit({ ...draft, printers: draft.printers.map(printer => printer.id === printerId ? { ...printer, ...patch } : printer) });
	const printers = draft ? [...snapshot.printers.filter(printer => printer.legacy), ...draft.printers] : [];
	const options = (selected, excluded = [], unavailable = false) => <>
		{selected && !printers.some(printer => printer.id === selected && printer.enabled) && <option value={selected} disabled>Unavailable printer ({selected})</option>}
		{printers.filter(printer => printer.enabled).map(printer => <option key={printer.id} value={printer.id} disabled={unavailable || excluded.includes(printer.id)}>
			{printer.label || "Unnamed printer"}{printer.legacy ? " (legacy)" : ""}
		</option>)}
	</>;
	const devices = snapshot ? [...snapshot.devices, ...draft.deviceDefaults.filter(item => !snapshot.devices.some(device => device.deviceId === item.deviceId))
		.map(item => ({ deviceId: item.deviceId, label: "Unavailable device", eligible: false }))] : [];
	return <section className="printer-routing" aria-labelledby={`${id}-heading`}>
		<header><h2 id={`${id}-heading`}>Shared printer library</h2>
			<button type="button" className="printer-routing-icon" title="Refresh printer routing" aria-label="Refresh printer routing" disabled={busy || scopeLost} onClick={reload}>&#8635;</button>
		</header>
		<p className="printer-routing-meta">{environment} / {restaurantId}</p>
		{busy && <p role="status">Loading or saving printer routing...</p>}
		{error && <p role="alert">{error}</p>}
		{message && <p role="status">{message}</p>}
		{draft && !scopeLost && <form onSubmit={save} noValidate aria-label="Shared printer routing">
			{stale && <p role="alert">Saved routing has a newer revision. Your draft is retained; saving is blocked until the draft is discarded.</p>}
			{validation && <p id={`${id}-validation`} role="alert">{validation}</p>}
			<div className="printer-routing-toolbar"><h3>Printers ({draft.printers.length}/20)</h3>
				<button type="button" disabled={busy || blocked || draft.printers.length >= 20} onClick={addPrinter}>Add printer</button>
			</div>
			{snapshot.printers.filter(printer => printer.legacy).map(printer => <div key={printer.id} className="printer-routing-legacy">
				<strong>{printer.label} (legacy, read-only)</strong>
				<span>{MODELS.find(model => model.value === printer.model).label} / {printer.identifier} / {printer.enabled ? "Enabled" : "Disabled"}</span>
			</div>)}
			{!draft.printers.length && <p>No shared printers configured.</p>}
			{draft.printers.map((printer, index) => <fieldset key={printer.id} className="printer-routing-row" disabled={busy || blocked}>
				<legend>Printer {index + 1}</legend>
				<label>Name<input aria-label={`Printer ${index + 1} name`} value={printer.label} required onChange={event => updatePrinter(printer.id, { label: event.target.value })} /></label>
				<label>Model<select aria-label={`Printer ${index + 1} model`} value={printer.model} onChange={event => updatePrinter(printer.id, { model: event.target.value })}>
					{MODELS.map(model => <option key={model.value} value={model.value}>{model.label}</option>)}
				</select></label>
				<label>Private LAN IPv4<input aria-label={`Printer ${index + 1} address`} value={printer.identifier} inputMode="decimal" required
					onChange={event => updatePrinter(printer.id, { identifier: event.target.value })} /></label>
				<label className="printer-routing-enabled"><input type="checkbox" aria-label={`Printer ${index + 1} enabled`} checked={printer.enabled}
					onChange={event => updatePrinter(printer.id, { enabled: event.target.checked })} />Enabled</label>
				<button type="button" className="printer-routing-icon" title={`Remove printer ${index + 1}`} aria-label={`Remove printer ${index + 1}`}
					onClick={() => edit({ ...draft, printers: draft.printers.filter(item => item.id !== printer.id) })}>&#215;</button>
			</fieldset>)}
			<fieldset className="printer-routing-targets" disabled={busy || blocked}>
				<legend>Routing</legend>
				<label>Default printer<select aria-label="Default printer" value={draft.defaultPrinterId || ""} onChange={event => edit({ ...draft, defaultPrinterId: event.target.value || null })}>
					<option value="" disabled={printers.some(printer => printer.enabled)}>No default</option>{options(draft.defaultPrinterId)}
				</select></label>
				{[0, 1, 2].map(index => <label key={index}>Backup {index + 1}<select aria-label={`Backup ${index + 1}`} value={draft.backupPrinterIds[index] || ""} onChange={event => {
					const backups = Array.from({ length: 3 }, (_, position) => draft.backupPrinterIds[position] || "");
					backups[index] = event.target.value; edit({ ...draft, backupPrinterIds: backups });
				}}><option value="">No backup</option>{options(draft.backupPrinterIds[index], [draft.defaultPrinterId, ...draft.backupPrinterIds.filter((_, position) => position !== index)])}</select></label>)}
			</fieldset>
			<h3>Device defaults ({draft.deviceDefaults.length}/100)</h3>
			{!devices.length && <p>No enrolled devices.</p>}
			<div className="printer-routing-devices">{devices.map(device => {
				const assigned = draft.deviceDefaults.find(item => item.deviceId === device.deviceId)?.printerId || "";
				return <label key={device.deviceId} className="printer-routing-device">{device.label || "Unnamed device"}
					<span className="printer-routing-meta">{device.deviceId}{!device.eligible && " / Ineligible"}</span>
					<select aria-label={`Default for ${device.label || "Unnamed device"} (${device.deviceId})`} value={assigned}
						disabled={busy || blocked || (!assigned && (!device.eligible || draft.deviceDefaults.length >= 100))} onChange={event => {
						const defaults = draft.deviceDefaults.filter(item => item.deviceId !== device.deviceId);
						if (event.target.value) defaults.push({ deviceId: device.deviceId, printerId: event.target.value });
						edit({ ...draft, deviceDefaults: defaults });
					}}><option value="">Restaurant default</option>{options(assigned, [], !device.eligible)}</select>
				</label>;
			})}</div>
			<label className="printer-routing-reason">Support reason<input aria-label="Routing support reason" value={reason} maxLength={240} required disabled={busy || blocked}
				onChange={event => { if (!pending.current && current(generation.current.value)) { reasonRef.current = event.target.value; setReason(event.target.value); setMessage(""); } }} /></label>
			<div className="printer-routing-actions">
				<button type="submit" disabled={busy || blocked || stale || !dirty || !!validation || !reasonValid}>Save routing</button>
				{dirty && <button type="button" disabled={busy || blocked} onClick={() => { if (current(generation.current.value) && !pending.current) { apply(snapshot); setError(""); setMessage(""); } }}>Discard draft and load saved routing</button>}
			</div>
		</form>}
	</section>;
}

function AuthenticatedRouting({ restaurantId, environment, projectId }) {
	const [user, loading, error] = useAuthState(auth);
	const actor = useRef({ revision: 0 });
	if (actor.current.user !== user || actor.current.client !== functions || actor.current.authClient !== auth ||
		actor.current.app !== functions.app || actor.current.authApp !== auth.app) {
		actor.current = { user, client: functions, authClient: auth, app: functions.app, authApp: auth.app, revision: actor.current.revision + 1 };
	}
	if (loading) return <p role="status">Checking support access...</p>;
	if (error || !user || auth.currentUser !== user || (auth.app && auth.app.options.projectId !== projectId)) return <p role="alert">Sign in with an authorized support account.</p>;
	return <RoutingForm key={JSON.stringify([restaurantId, environment, projectId, user.uid, actor.current.revision])}
		restaurantId={restaurantId} environment={environment} projectId={projectId} user={user} client={functions} authClient={auth} />;
}

export default function RestaurantPrinterRoutingControls({ restaurantId }) {
	const environment = getStaffProvisioningEnvironment();
	if (!restaurantId || !environment) return null;
	return <AuthenticatedRouting restaurantId={restaurantId} environment={environment} projectId={functions.app.options.projectId} />;
}
